import React, { useEffect, useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { API } from '../../../Services/axios'
import { setSelectedProject } from '../../../Redux/Slice/selectedProjectSlice'
import { setStep } from '../../../Redux/Slice/stepSlice'
import SkeletonLoader from '../../../Components/SkeletonLoader'

const Home = () => {
  const [projects, setProjects] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [openFaqIndex, setOpenFaqIndex] = useState(null)
  const swiperRef = useRef(null)
  const swiperInstanceRef = useRef(null)
  const navigate = useNavigate()
  const dispatch = useDispatch()

  useEffect(() => {
    setIsLoading(true)
    API.get('/project/')
      .then((resp) => {
        setProjects(resp.data.results || [])
        console.log("projects:", resp.data.results)
      })
      .catch((error) => {
        console.log("erreur:", error)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [])

  const convertText = (text) => {
    if (!text) return text
    let convertedText = text.replace(/_/g, ' ').toLowerCase()
    convertedText = convertedText.charAt(0).toUpperCase() + convertedText.slice(1)
    return convertedText
  }

  const handleProjectClick = (project) => {
    if (project.steps && project.steps.length > 0) {
      dispatch(setStep(project.steps[0]))
      dispatch(setSelectedProject(project))
      navigate(`/projet/${project.id}`)
    }
  }

  // Initialiser Swiper après le montage du composant (comme dans indes.html)
  useEffect(() => {
    const initSwiper = () => {
      // Vérifier que Swiper est disponible et que le ref existe
      if (typeof window !== 'undefined' && window.Swiper && swiperRef.current) {
        // Détruire l'instance existante si elle existe
        if (swiperInstanceRef.current) {
          try {
            swiperInstanceRef.current.destroy(true, true)
          } catch (e) {
            console.warn('Erreur lors de la destruction de l\'ancienne instance Swiper:', e)
          }
          swiperInstanceRef.current = null
        }

        // Trouver l'élément de pagination
        const paginationEl = swiperRef.current.querySelector('.swiper-pagination')
        
        if (paginationEl) {
          try {
            // Initialiser Swiper exactement comme dans indes.html
            swiperInstanceRef.current = new window.Swiper(swiperRef.current, {
              direction: 'horizontal',
              loop: true,
              slidesPerView: 2,
              spaceBetween: 25,
              pagination: {
                el: paginationEl,
                dynamicBullets: true,
                clickable: true,
              },
            })
            console.log('✅ Swiper initialisé avec succès')
          } catch (error) {
            console.error('❌ Erreur lors de l\'initialisation du Swiper:', error)
          }
        } else {
          console.warn('⚠️ Élément .swiper-pagination non trouvé')
        }
      }
    }

    // Fonction pour vérifier et initialiser
    const checkAndInit = () => {
      if (typeof window !== 'undefined' && window.Swiper) {
        // Attendre que le DOM soit complètement rendu
        setTimeout(() => {
          initSwiper()
        }, 100)
      }
    }

    // Si Swiper est déjà chargé
    if (typeof window !== 'undefined' && window.Swiper) {
      checkAndInit()
    } else {
      // Attendre que Swiper soit chargé
      const checkSwiper = setInterval(() => {
        if (typeof window !== 'undefined' && window.Swiper) {
          clearInterval(checkSwiper)
          checkAndInit()
        }
      }, 50)

      // Timeout de sécurité (5 secondes)
      const timeout = setTimeout(() => {
        clearInterval(checkSwiper)
        checkAndInit()
      }, 5000)

      return () => {
        clearInterval(checkSwiper)
        clearTimeout(timeout)
      }
    }

    // Cleanup
    return () => {
      if (swiperInstanceRef.current) {
        try {
          swiperInstanceRef.current.destroy(true, true)
          swiperInstanceRef.current = null
        } catch (error) {
          console.error('Erreur lors de la destruction du Swiper:', error)
        }
      }
    }
  }, [])

  return (
    <main>
      <div className="w-full max-w-screen-xl mx-auto">
        {/* Breadcrumb */}
        <nav className="container mx-auto p-6" aria-label="Fil d'Ariane">
          <ul className="page-breadcrumb flex gap-2 items-center justify-start" style={{ color: '#4B4B4A' }}>
            <li className="underline">
              <Link to="/" className="cursor-pointer" style={{ color: '#4B4B4A' }}>Accueil</Link>
            </li>
            <li aria-hidden="true">›</li>
            <li aria-current="page" style={{ color: '#4B4B4A' }}>Outils en ligne</li>
          </ul>
        </nav>

        <div className="container mx-auto p-6 grid gap-12">
          {/* Titre principal */}
          <h1 className="text-4xl font-semibold leading-14" style={{ color: '#4B4B4A' }}>
            Outils en ligne : calculateurs & demandes d'études
          </h1>

          {/* Introduction */}
          <div className="grid gap-2" style={{ color: '#4B4B4A' }}>
            <p className="font-bold">
              Calculez, estimez et préparez vos projets bois avec le savoir-faire Rullier Bois.
            </p>
            <p>
              Parce qu'un projet bois mérite précision et sérénité, nous mettons à votre disposition une sélection d'outils en ligne pour vous accompagner à chaque étape : calculs techniques, estimations de quantités, demandes de chiffrages ou de devis personnalisés.
              Que vous travailliez sur un bardage, une terrasse, un solivage, ou une charpente traditionnelle, nos outils vous permettent d'obtenir rapidement les informations essentielles pour avancer plus vite et plus juste.
            </p>
            <p className="font-bold">
              Gagnez du temps, sécurisez vos choix, et profitez de l'expertise Rullier Bois pour concevoir vos projets en toute confiance.
            </p>
          </div>

          {/* Section Calculateurs */}
          <div className="grid grid-cols-1 lg:grid-cols-7 gap-16 mt-6">
            <div className="col-span-1 lg:col-span-2" style={{ height: '24rem' }}>
              <img 
                src="/assets/images/calculateurs.jpg" 
                alt="Illustration calculateurs Rullier Bois"
                className="h-full w-full object-cover rounded-2xl"
                style={{ 
                  borderRadius: '1rem',
                  height: '100%',
                  width: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>
            <div className="col-span-1 lg:col-span-5">
              <div className="grid gap-8">
                <h2 className="text-3xl" style={{ color: '#4B4B4A' }}>
                  Nos calculateurs pour tous vos projets
                </h2>
                {isLoading ? (
                  <div className="grid grid-cols-2 xl:grid-cols-4 gap-6">
                    {Array.from({ length: 4 }, (_, index) => (
                      <div key={index} className="col-span-1 w-full">
                        <SkeletonLoader type="project" width="100%" height="280px" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 xl:grid-cols-4 gap-6">
                    {projects.map((project) => (
                      <div
                        key={project.id}
                        className="col-span-1 w-full group cursor-pointer"
                        onClick={() => handleProjectClick(project)}
                      >
                        <div 
                          className="w-full rounded-t-2xl overflow-hidden"
                          style={{ height: '11rem' }}
                        >
                          <img
                            src={project.image || "/assets/images/platre.jpg"}
                            alt={`illustration ${convertText(project.name)}`}
                            className="h-full w-full object-cover transition-transform duration-300 ease-out"
                            style={{
                              height: '100%',
                              width: '100%',
                              objectFit: 'cover',
                              transform: 'scale(1)',
                              transition: 'transform 0.3s ease-out',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'scale(1.05) translateY(-4px)'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'scale(1)'
                            }}
                          />
                        </div>
                        <div
                          className="w-full px-4 py-6 border-x border-b rounded-b-2xl grid items-center"
                          style={{
                            height: '8rem',
                            borderColor: 'rgba(75, 75, 74, 0.1)',
                            backgroundColor: 'transparent',
                            transition: 'background-color 0.3s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(152, 131, 99, 0.05)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent'
                          }}
                        >
                          <h3 className="text-center" style={{ color: '#4B4B4A' }}>
                            Calculez vos besoins en{' '}
                            <span className="font-semibold">{convertText(project.name)}</span>
                          </h3>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section Bureau d'Étude */}
          <div className="grid grid-cols-1 lg:grid-cols-7 gap-16 mt-12">
            <div className="col-span-1 lg:col-span-2" style={{ height: '24rem' }}>
              <img
                src="/assets/images/rullier-bois-bureau-d-etude.jpg"
                alt="Illustration Bureau d'Études Rullier Bois"
                className="h-full w-full object-cover rounded-2xl"
                style={{ 
                  borderRadius: '1rem',
                  height: '100%',
                  width: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>
            <div className="col-span-1 lg:col-span-5">
              <div className="grid gap-8">
                <h2 className="text-3xl" style={{ color: '#4B4B4A' }}>
                  Les outils en ligne de notre Bureau d'Étude construction bois
                </h2>
                <div className="grid grid-cols-2 xl:grid-cols-4 gap-6">
                  <Link to="https://rullier.be.ova-consulting.fr/" className="col-span-1 w-full group">
                    <div 
                      className="w-full rounded-t-2xl overflow-hidden"
                      style={{ height: '11rem' }}
                    >
                      <img
                        src="/assets/images/charpente-traditionnelle.jpg"
                        alt="Illustration demande d'études Rullier Bois"
                        className="h-full w-full object-cover transition-transform duration-300 ease-out"
                        style={{
                          height: '100%',
                          width: '100%',
                          objectFit: 'cover',
                          transform: 'scale(1)',
                          transition: 'transform 0.3s ease-out',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.05) translateY(-4px)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)'
                        }}
                      />
                    </div>
                    <div
                      className="w-full px-4 py-6 border-x border-b rounded-b-2xl grid items-center cursor-pointer"
                      style={{
                        height: '8rem',
                        borderColor: 'rgba(75, 75, 74, 0.1)',
                        backgroundColor: 'transparent',
                        transition: 'background-color 0.3s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(152, 131, 99, 0.05)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent'
                      }}
                    >
                      <h3 className="text-center" style={{ color: '#4B4B4A' }}>
                        Demande d'étude / Chiffrage
                      </h3>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section avec slider */}
        <div className="w-full mt-24" style={{ backgroundColor: 'rgba(152, 131, 99, 0.05)' }}>
          <div className="w-full max-w-screen-xl mx-auto py-24">
            <div className="container mx-auto p-6">
              <h2 className="text-4xl leading-12" style={{ color: '#988363' }}>
                <span className="font-bold">Simplifiez vos projets</span> avec Rullier Bois
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-7 items-end gap-16 mt-12">
                <div className="col-span-1 lg:col-span-3" style={{ height: '24rem' }}>
                  <div className="swiper" ref={swiperRef} style={{ height: '100%' }}>
                    <div className="swiper-wrapper">
                      <div className="swiper-slide">
                        <div style={{ height: '24rem' }}>
                          <img
                            src="/assets/images/rullierbois-histoire.jpg"
                            alt="photo historique rullier bois"
                            className="h-full w-full object-cover rounded-2xl"
                            style={{ 
                              borderRadius: '1rem',
                              height: '100%',
                              width: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        </div>
                      </div>
                      <div className="swiper-slide">
                        <div style={{ height: '24rem' }}>
                          <img
                            src="/assets/images/rullier-140ans.jpg"
                            alt="photo 140ans rullier bois"
                            className="h-full w-full object-cover rounded-2xl"
                            style={{ 
                              borderRadius: '1rem',
                              height: '100%',
                              width: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        </div>
                      </div>
                      <div className="swiper-slide">
                        <div style={{ height: '24rem' }}>
                          <img
                            src="/assets/images/bureau-logiciel-bureau-etude.jpg"
                            alt="photo logiciel bureau etude"
                            className="h-full w-full object-cover rounded-2xl"
                            style={{ 
                              borderRadius: '1rem',
                              height: '100%',
                              width: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        </div>
                      </div>
                      <div className="swiper-slide">
                        <div style={{ height: '24rem' }}>
                          <img
                            src="/assets/images/charpente-rullier.jpg"
                            alt="photo charpente traditionnelle rullier bois"
                            className="h-full w-full object-cover rounded-2xl"
                            style={{ 
                              borderRadius: '1rem',
                              height: '100%',
                              width: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="swiper-pagination"></div>
                  </div>
                </div>
                <div className="col-span-1 lg:col-span-4">
                  <div className="grid gap-8" style={{ color: '#4B4B4A' }}>
                    <p>
                      Depuis plus de 130 ans, Rullier Bois accompagne les professionnels et les particuliers dans leurs projets bois. Pour aller encore plus loin dans l'efficacité et la précision, nous mettons à votre disposition une sélection d'outils en ligne dédiés :{' '}
                      <span className="font-semibold">calculateurs techniques, estimations de quantités, demandes de chiffrages et devis personnalisés.</span>
                    </p>
                    <p>
                      S'appuyant sur l'expérience de notre{' '}
                      <Link to="https://rullier.be.ova-consulting.fr/" style={{ color: '#4B4B4A' }}>
                        Bureau d'Étude, spécialiste de la construction bois depuis plus de 20 ans
                      </Link>
                      , ces outils vous permettent d'obtenir rapidement des informations fiables, quel que soit votre projet :{' '}
                      <span className="font-semibold">bardage, terrasse, solivage, charpente traditionnelle, et bien plus encore.</span>
                    </p>
                    <div className="w-full flex justify-start">
                      <Link
                        to="/a-propos"
                        className="w-fit rounded-full py-2 px-4 flex justify-between items-center border gap-4"
                        style={{
                          borderColor: 'rgba(152, 131, 99, 0.1)',
                          color: '#988363',
                          transition: 'all 0.3s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'rgba(152, 131, 99, 0.7)'
                          e.currentTarget.style.color = 'white'
                          e.currentTarget.style.transform = 'translateY(-4px)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent'
                          e.currentTarget.style.color = '#988363'
                          e.currentTarget.style.transform = 'translateY(0)'
                        }}
                      >
                        <span className="text-sm">En savoir plus</span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                          className="size-4"
                          style={{ width: '1rem', height: '1rem' }}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                          />
                        </svg>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section étapes */}
        <div className="container mx-auto px-6 py-24 section-etapes">
          <h2 className="text-4xl leading-12" style={{ color: '#4B4B4A' }}>
            Nous vous guidons de <span className="font-semibold"><br />vos premières idées</span>{' '}
            <br />
            jusqu'à <span className="font-semibold">la réalisation finale.</span>.
          </h2>
          <div 
            className="grid grid-cols-1 lg:grid-cols-2 gap-16 relative"
            style={{ 
              marginTop: '4rem',
              marginBottom: '6rem',
            }}
          >
            {/* Image à gauche - sticky sur desktop */}
            <div 
              className="col-span-1 image-sticky lg:h-84"
              style={{
                height: 'auto',
              }}
            >
              <img
                src="/assets/images/rullier-bois-bureau-d-etude-illustration.jpg"
                alt="Illustration Bureau d'études bois"
                className="h-full w-full object-cover rounded-2xl"
                style={{ 
                  borderRadius: '1rem',
                  height: '100%',
                  width: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>
            {/* Texte à droite */}
            <div 
              className="col-span-1 grid gap-8 section-etapes-content"
              style={{
                paddingLeft: '0',
                paddingRight: '0',
              }}
            >
              {[
                {
                  number: '1.',
                  title: 'Comprendre & Anticiper',
                  text: "Nos outils en ligne vous donnent une première vision claire de votre projet : estimations, quantités, faisabilité.",
                },
                {
                  number: '2.',
                  title: 'Imaginer & Concevoir',
                  text: "Vous posez vos idées, nos calculateurs les structurent. Le Bureau d'Études transforme ensuite ces intentions en solutions techniques viables.",
                },
                {
                  number: '3.',
                  title: 'Transformer & Réaliser',
                  text: "À partir de vos données, Rullier Bois fabrique, prépare et organise la livraison de vos matériaux ou kits sur mesure.",
                },
                {
                  number: '4.',
                  title: 'Accompagner & Sécuriser',
                  text: "Nos outils vous guident, et notre équipe reste là pour optimiser vos choix, répondre à vos questions et sécuriser chaque étape.",
                },
                {
                  number: '5.',
                  title: 'Finaliser & Valoriser',
                  text: "Avec des bases solides et des matériaux adaptés, votre chantier avance plus vite et votre structure gagne en durabilité et précision.",
                },
              ].map((step, index) => (
                <div key={index}>
                  <h3 className="text-3xl mb-1" style={{ color: '#988363' }}>
                    <span className="font-semibold text-7xl">{step.number}</span>
                    <br />
                    {step.title}
                  </h3>
                  <p style={{ color: '#4B4B4A' }}>{step.text}</p>
                </div>
              ))}
              <div className="w-full grid gap-6 justify-start mt-12">
                <Link
                  to="https://rullier.be.ova-consulting.fr/"
                  className="w-fit rounded-full py-2 px-4 flex justify-between items-center border gap-4"
                  style={{
                    borderColor: 'rgba(152, 131, 99, 0.1)',
                    backgroundColor: 'rgba(152, 131, 99, 0.7)',
                    color: 'white',
                    transition: 'all 0.3s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent'
                    e.currentTarget.style.color = '#988363'
                    e.currentTarget.style.transform = 'translateY(-4px)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(152, 131, 99, 0.7)'
                    e.currentTarget.style.color = 'white'
                    e.currentTarget.style.transform = 'translateY(0)'
                  }}
                >
                  <span className="text-sm">Démarrez votre demande de chiffrage Bureau d'Études</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-4"
                    style={{ width: '1rem', height: '1rem' }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                    />
                  </svg>
                </Link>
                {/* <Link
                  to="/services"
                  className="w-fit rounded-full py-2 px-4 flex justify-between items-center border gap-4"
                  style={{
                    borderColor: 'rgba(152, 131, 99, 0.1)',
                    color: '#988363',
                    transition: 'all 0.3s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(152, 131, 99, 0.7)'
                    e.currentTarget.style.color = 'white'
                    e.currentTarget.style.transform = 'translateY(-4px)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent'
                    e.currentTarget.style.color = '#988363'
                    e.currentTarget.style.transform = 'translateY(0)'
                  }}
                >
                  <span className="text-sm">Consultez nos autres services</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-4"
                    style={{ width: '1rem', height: '1rem' }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                    />
                  </svg>
                </Link> */}
              </div>
            </div>
          </div>
        </div>

        {/* Section FAQ */}
        <div className="container mx-auto px-6 pt-16 pb-24" style={{ borderTop: '1px solid rgba(75, 75, 74, 0.1)' }}>
          <div className="grid grid-cols-1 items-center justify-between gap-12">
            <h2 className="text-4xl leading-12 text-center" style={{ color: '#4B4B4A' }}>
              <span className="font-semibold">Toutes vos questions</span> sur les outils en ligne Rullier Bois
            </h2>
            <section className="mx-auto w-full space-y-5">
              {[
                {
                  question: "À quoi servent les outils en ligne Rullier Bois ?",
                  answer: "Les outils en ligne Rullier Bois permettent d'estimer rapidement vos besoins en matériaux et de lancer une demande d'étude bois. Ils sont conçus pour accompagner les professionnels et particuliers en Poitou-Charentes et en Nouvelle-Aquitaine, du chiffrage à la préparation de projet.",
                },
                {
                  question: "Quels outils de calcul sont disponibles sur le site Rullier Bois ?",
                  answer: "Rullier Bois met à disposition plusieurs calculateurs en ligne, notamment pour calculer vos besoins en plaques de plâtre, en revêtements de sol et en bardage. Ces outils vous aident à estimer les quantités nécessaires avant l'achat ou la demande de devis.",
                },
                {
                  question: "Le calculateur de plaques de plâtre est-il adapté aux projets locaux ?",
                  answer: "Oui. Le calculateur de plaques de plâtre permet d'estimer les surfaces et quantités nécessaires pour des projets de rénovation ou de construction en Poitou-Charentes, en tenant compte des usages courants sur les chantiers locaux.",
                },
                {
                  question: "À quoi sert le calculateur de besoins en sol ?",
                  answer: "Le calculateur de sol permet d'évaluer rapidement les surfaces et quantités de matériaux nécessaires pour vos projets d'aménagement intérieur ou extérieur, en maison individuelle, logement collectif ou local professionnel.",
                },
                {
                  question: "Comment utiliser le calculateur de bardage bois ?",
                  answer: "Le calculateur de bardage bois vous permet d'estimer les quantités nécessaires selon les surfaces à couvrir. Il est particulièrement utile pour les projets de façades bois en Poitou-Charentes et Nouvelle-Aquitaine, où le choix des sections et essences est déterminant.",
                },
                {
                  question: "Les calculateurs remplacent-ils un devis ou une étude technique ?",
                  answer: "Non. Les calculateurs fournissent une estimation rapide et pratique. Pour un chiffrage précis ou une analyse structurelle, il est recommandé de compléter avec une demande d'étude ou un devis auprès des équipes Rullier Bois.",
                },
                {
                  question: "À qui s'adressent les outils en ligne Rullier Bois ?",
                  answer: "Les outils en ligne s'adressent aux artisans, professionnels du bâtiment, maîtres d'œuvre et particuliers, souhaitant préparer un projet ou estimer leurs besoins en matériaux en Poitou-Charentes et en Nouvelle-Aquitaine.",
                },
                {
                  question: "Que faire après avoir utilisé un calculateur ou une demande d'étude ?",
                  answer: "Après utilisation d'un outil en ligne, vous pouvez affiner votre projet en contactant une agence Rullier Bois ou en déposant une demande de devis ou d'étude pour un accompagnement personnalisé et local.",
                },
                {
                  question: "Les outils en ligne Rullier Bois sont-ils gratuits ?",
                  answer: "Oui. Les calculateurs et outils de demande d'étude sont accessibles gratuitement afin de vous aider à préparer votre projet bois ou matériaux, avant toute prise de décision.",
                },
              ].map((faq, index) => {
                const isOpen = openFaqIndex === index
                return (
                  <details
                    key={index}
                    className="group border rounded-2xl p-6"
                    open={isOpen}
                    style={{
                      borderColor: 'rgba(75, 75, 74, 0.1)',
                      borderRadius: '1rem',
                    }}
                    onToggle={(e) => {
                      setOpenFaqIndex(e.currentTarget.open ? index : null)
                    }}
                  >
                    <summary
                      className="flex cursor-pointer list-none items-center justify-between"
                      style={{ 
                        listStyle: 'none',
                        display: 'flex',
                        cursor: 'pointer',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                      onClick={(e) => {
                        e.preventDefault()
                        setOpenFaqIndex(isOpen ? null : index)
                      }}
                    >
                      <span className="text-lg" style={{ color: '#4B4B4A' }}>
                        {faq.question}
                      </span>
                      <span
                        className="transition-transform text-2xl leading-none"
                        style={{
                          color: '#4B4B4A',
                          transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s ease',
                          fontSize: '1.5rem',
                          lineHeight: '1',
                        }}
                      >
                        +
                      </span>
                    </summary>
                    <div 
                      className="mt-3" 
                      style={{ 
                        color: '#4B4B4A',
                        marginTop: '0.75rem',
                      }}
                    >
                      {faq.answer}
                    </div>
                  </details>
                )
              })}
            </section>
          </div>
        </div>
      </div>

    </main>
  )
}

export default Home
